// ViewAllReport.js - Component to display a comprehensive report of all bookings, including filters for client and site, 
// search functionality, and the ability to export the report to Excel.
import Header from "../common/Header";
import SidebarIcons from "../common/SidebarIcons";
import Sidebar from "../common/Sidebar";
import { useState, useEffect } from "react";
import { useNavigate, generatePath, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Footer from "../common/Footer";
import http from "../../api/server.js";
import {
  GET_BOOKINGS_REPORT,
  GET_RMS_PROPERTIES_AND_CLIENT_COMPANIES_LIST,
  GET_EXCLE_REPORT,
} from "../../api/apiPath.js";
import DynamicTable from "../common/DynamicTable";
import actionSearchIcon from "../../assets/img/actionSearchIcon.png";
import dashlogo from "../../assets/img/dashbarlogo.png";
import useGetClientId from "../../hooks/useGetClientId";
import Tooltip from "@mui/material/Tooltip";
import LoadingComponent from "../common/LoadingComponent";
import Toaster from "../common/Toaster";
import AutocompleteInput from "../common/AutocompleteInput";
import ClearIcon from "@mui/icons-material/Clear";
import searchIcon from "../../assets/img/searchIcon.png";
import { IconButton } from "@mui/material";
import downloadIcon from "../../assets/img/excleExport.png";
import StatusPill from "../common/StatusPill.js";

export default function ViewAllReport() {
  const { userObj } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { clientNameParam } = useParams();
  
  const clientObj = useGetClientId();
  const [hideSidebar, setHideSidebar] = useState(false);
  const [clientOptions, setClientoptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [filteredSiteOptions, setFilteredSiteOptions] = useState([]);
  const [bookingOptions, setBookingOptions] = useState({
    propertyValue: "",
    clientValue: "",
  });
  const [bookingreportData, setBookingReportData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [toastData, setToastData] = useState({
    toastShow: false,
    toastType: "",
    toastString: "",
  });
  const closeButton = () => {
    setToastData({
      toastShow: false,
      toastData: "",
      toastType: "",
    });
  };

  const [toolbar, setToolBar] = useState({
    searchText: "",
    name: "Name",
    IsAcknowledged: "Acknowledged",
    reminder: "Reminder Sent",
    booking: "Bookings Done",
    letter: "Letter Sent",
  });

  /* =========================
     Filter functions
     ========================= */
  useEffect(() => {
    if (bookingreportData) {
      const tempfilteredRows = bookingreportData?.filter((row) => {
        const matchesSearch =
          !toolbar.searchText ||
          row.ResidentName?.toLowerCase().includes(
            toolbar.searchText.toLowerCase(),
          ) ||
          row.Email?.toLowerCase().includes(toolbar.searchText.toLowerCase()) ||
          row.PropertyName?.toLowerCase().includes(
            toolbar.searchText.toLowerCase(),
          ) ||
          row.CommunicationStatus?.toLowerCase().includes(
            toolbar.searchText.toLowerCase(),
          ) ||
          // row.inspectionMasterId == toolbar.searchText ||
          row.SentBy?.toLowerCase().includes(
            toolbar.searchText.toLowerCase(),
          ) ||
          row.InspectionStatus?.toLowerCase().includes(
            toolbar.searchText.toLowerCase(),
          );

        const matchesPropertyName =
          toolbar.name === "Name" ||
          row.PropertyName?.toLowerCase() === toolbar.name.toLowerCase();

        const matchesReminder =
          toolbar.reminder === "Reminder Sent" ||
          row.IsReminderSent?.toLowerCase().includes(
            toolbar.reminder?.toLowerCase(),
          );

        const matchesAck =
          toolbar.IsAcknowledged === "Acknowledged" ||
          row.IsAcknowledged?.toLowerCase().includes(
            toolbar.IsAcknowledged.toLowerCase(),
          );
        const matchesBooking =
          toolbar.booking === "Bookings Done" ||
          row.BookingDone?.toLowerCase().includes(
            toolbar.booking.toLowerCase(),
          );
        const matchesLetter =
          toolbar.letter === "Letter Sent" ||
          row.LetterSent?.toLowerCase().includes(toolbar.letter.toLowerCase());

        return (
          matchesSearch &&
          matchesPropertyName &&
          matchesReminder &&
          matchesAck &&
          matchesBooking &&
          matchesLetter
        );
      });
      setFilteredRows(tempfilteredRows);
    } else {
      setFilteredRows(bookingreportData);
    }
  }, [bookingreportData, toolbar]);

  /* =========================
     FETCH Table DATA
     ========================= */
  useEffect(() => {
    if (clientObj.clientId) {
      handleGetAllRMSPropertyByClinetID(clientObj.clientId);
    }
  }, [clientObj.clientId, clientObj.loading]);

  const handleGetAllRMSPropertyByClinetID = async (clientId) => {
    try {
      const response = await http.get(
        GET_RMS_PROPERTIES_AND_CLIENT_COMPANIES_LIST + `?clientId=${clientId}`,
      );
      if (response.status === 200) {
        console.log("RMS Properties and Client Companies List", response.data);
        const properties = response?.data?.properties || [];
        const clients = response?.data?.clients || [];
        setClientoptions(clients);
        setSiteOptions(properties);
        // const firstClient = clients?.filter((client) => client.ClientId === clientObj?.clientId)[0];
        // const filteredProperties = properties.filter(
        //   (property) => property.ClientName === firstClient?.ClientName,
        // );
        // setFilteredSiteOptions(filteredProperties);
        setFilteredSiteOptions(properties);
        setBookingOptions({
          ...bookingOptions,
          clientValue: clientObj?.clientId || "",
        });
        handleGetAllBookingReport({
          propertyValue: "",
          clientValue: clientObj?.clientId || "",
        });
      }
    } catch (error) {
      console.error(
        "Error fetching RMS Properties and Client Companies List",
        error,
      );
    }
  };

  const handleGetAllBookingReport = async (options) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("clientId", options.clientValue);
      if (options.propertyValue)
        params.append("propertyMasterId", options.propertyValue);

      const queryString = params.toString();

      const response = await http.get(GET_BOOKINGS_REPORT + `?${queryString}`);
      if (response.status === 200) {
        console.log("Bookings Report", response.data);
        const formattedProperties = (response?.data?.properties || []).map(
          (item, index) => ({
            ...item,
            id: index + 1, // Add an 'id' field for DataGrid
            EmailSentOn: item.EmailSentOn
              ? new Date(item.EmailSentOn).toLocaleDateString("en-GB")
              : "N/A",
            ManualFollowUpOn: item.ManualFollowUpOn
              ? new Date(item.ManualFollowUpOn).toLocaleDateString("en-GB")
              : "N/A",
            BookingDate: item.BookingDate
              ? new Date(item.BookingDate).toLocaleDateString("en-GB")
              : "N/A",
            IsReminderSent: item.IsReminderSent ? "Yes" : "No",
            IsAcknowledged: item.IsAcknowledged ? "Yes" : "No",
            LetterSent: item.IsLetterServed ? "Yes" : "No",
          }),
        );
        setBookingReportData(formattedProperties);
        setSummary(response?.data?.summary[0] || {});
      }
    } catch (error) {
      console.error("Error fetching Bookings Report", error);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Export to excel
     ========================= */
  const handleExportToExcel = async () => {
     if (!filteredRows || filteredRows.length === 0) {
    setToastData({
      toastShow: true,
      toastType: "failure",
      toastString: "No data to export",
    });
    return;
  }
    try {
      setLoading(true);

      const clientId = bookingOptions.clientValue || clientObj?.clientId;
      const propertyMasterId = bookingOptions.propertyValue || "";

      const params = new URLSearchParams();

      if (clientId) params.append("clientId", clientId);
      if (propertyMasterId) params.append("propertyMasterId", propertyMasterId);

      const response = await http.get(
        `${GET_EXCLE_REPORT}?${params.toString()}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const contentDisposition = response.headers["content-disposition"];
      let fileName = "BookingsReport.xlsx";

      if (contentDisposition) {
        const match = contentDisposition.match(
          /filename\*?=(?:UTF-8'')?([^;]+)/,
        );
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1].replace(/"/g, ""));
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      setToastData({
        toastShow: true,
        toastType: "success",
        toastString: "Excel file downloaded successfully.",
      });
    } catch (err) {
      console.error(err);
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to download Excel file",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Table columns
     ========================= */
  const tableColumns = [
    {
      field: "ResidentName",
      height: 50,
      minWidth: 200,
      headerName: "RESIDENT NAME",
      headerClassName: "super-app-theme--header",
      editable: false,
      sortable: true,
      flex: 1,
      type: "string",
    },
    {
      field: "Email",
      height: 50,
      minWidth: 250,
      headerName: "EMAIL",
      headerClassName: "super-app-theme--header",
      editable: false,
      sortable: true,
      flex: 1,
      type: "string",
    },
    {
      field: "PropertyName",
      headerName: "SITE NAME",
      width: 150,
      height: 90,
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 150,
      flex: 1,
      renderCell: (data) => {
                    return(
                    <span 
                      onClick={() => {
                        if(!userObj?.isAdmin) return;
                        const path = generatePath("/client/:clientNameParam/viewSite/:propertyMasterId", {
                          clientNameParam,
                          propertyMasterId: data?.row?.PropertyMasterId
                        });
                        navigate(path);
                      }}
                      style={{cursor: "pointer"}}
                    >
                        {data?.row?.PropertyName}
                      </span>
                  )
                  }
    },
    {
      field: "BuildingName",
      headerName: "BUILDING NAME",
      width: 180,
      height: 50,
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 180,
      flex: 1,
      renderCell: (data) => {
                    return(
                    <span 
                      onClick={() => {
                        if(!userObj?.isAdmin) return;
                        const path = generatePath("/client/:clientNameParam/viewSite/:propertyMasterId", {
                          clientNameParam,
                          propertyMasterId: data?.row?.PropertyMasterId
                        });
                        navigate(path);
                      }}
                      style={{cursor: "pointer"}}
                    >
                        {data?.row?.BuildingName}
                      </span>
                  )
                  }
    },
    {
      field: "PropertyLocation",
      headerName: "SITE LOCATION",
      width: 180,
      height: 50,
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 180,
      flex: 1,
    },

    {
      field: "CommunicationStatus",
      headerName: "COMMUNICATION STATUS",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      width: 240,
      minWidth: 240,
      flex: 1,
    },

    {
      field: "SentBy",
      width: 200,
      height: 50,
      headerName: "SENT BY",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "EmailSentOn",
      width: 210,
      height: 50,
      headerName: "LAST ACTIONED ON",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 210,
      flex: 1,
    },
    {
      field: "IsReminderSent",
      width: 160,
      height: 50,
      headerName: "REMINDER SENT",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "ManualFollowUpOn",
      width: 210,
      height: 50,
      headerName: "MANUAL FOLLOW-UP ON",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 210,
      flex: 1,
    },
    {
      field: "IsAcknowledged",
      width: 160,
      height: 50,
      headerName: "ACKNOWLEDGED",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "BookingDone",
      width: 160,
      height: 50,
      headerName: "BOOKING DONE",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "BookingDate",
      width: 160,
      height: 50,
      headerName: "BOOKING DATE",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 160,
      flex: 1,
    },
    {
      field: "InspectionStatus",
      width: 180,
      height: 50,
      headerName: "INSPECTION STATUS",
      headerClassName: "super-app-theme--header",
      editable: false,
      type: "string",
      minWidth: 180,
      flex: 1,
      renderCell: (params) =>
        params.value ? <StatusPill status={params.value} row={params.row} /> : "N/A",
    },
  ];

  const isAnyFilterApplied =
    toolbar.searchText !== "" ||
    toolbar.name !== "Name" ||
    toolbar.IsAcknowledged !== "Acknowledged" ||
    toolbar.reminder !== "Reminder Sent" ||
    toolbar.booking !== "Bookings Done" ||
    toolbar.letter !== "Letter Sent" ||
    bookingOptions.clientValue !== clientObj?.clientId ||
    bookingOptions.propertyValue !== "";

  return (
    <>
      <Header hideSidebar={hideSidebar} setHideSidebar={setHideSidebar} />
      <Toaster
        show={toastData.toastShow}
        toastType={toastData.toastType}
        title={toastData.toastString}
        theme={"dark"}
        position="top-center"
        closeButton={closeButton}
      />
      <LoadingComponent loading={loading} />
      {userObj && (
        <div className="dashboard_body_main">
          {hideSidebar ? (
            <Sidebar
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          ) : (
            <SidebarIcons
              setHideSidebar={setHideSidebar}
              hideSidebar={hideSidebar}
            />
          )}

          <div className="d-flex dashboard_body flex-column py-1 w-100 mt-2">
            <div className="col-12 mb-3">
              <h3 className="fw-semibold  mb-0 text-start ">
                All Booking Reports
                {/* {console.log("client id",clientId)} */}
              </h3>

              <div
                className="card mt-4 p-3 rounded-4 mb-4 orange-box d-flex gap-3 flex-row align-items-center justify-content-between"
                style={{ height: "9.5rem" }}
              >
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-evenly text-center py-4">
                    <div>
                      <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                        {summary?.TotalBookingLinkSent || 0}
                      </h1>
                      <p className="fw-semibold mb-0">Booking Links Sent</p>
                    </div>
                    <div>
                      <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                        {summary?.TotalBookingDone || 0}
                      </h1>
                      <p className="fw-semibold mb-0"> Bookings Done</p>
                    </div>
                    <div className="col-6 col-sm-4 col-lg-2 mb-1">
                      <img src={dashlogo} alt="Inspector" height="100" />
                    </div>
                    <div>
                      <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                        {summary?.TotalEmailAcknowledge || 0}
                      </h1>
                      <p className="fw-semibold mb-0">Emails Acknowledged</p>
                    </div>
                    <div>
                      <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                        {summary?.TotalRemindersSent || 0}
                      </h1>
                      <p className="fw-semibold mb-0">Reminders Sent</p>
                    </div>
                    <div>
                      <h1 className="mb-0" style={{ fontSize: "4rem" }}>
                        {summary?.TotalLettersServed || 0}
                      </h1>
                      <p className="fw-semibold mb-0">Letters Served</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="row py-3 align-items-center g-2">
                <div className="col d-flex flex-wrap align-items-center gap-2">
                  <div className="col-12 col-sm-4">
                    <select
                      className="form-select custom_toolbar_control"
                      value={bookingOptions.clientValue}
                      onChange={(e) =>
                        setBookingOptions({
                          ...bookingOptions,
                          clientValue: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Client</option>
                      {clientOptions?.map((site) => (
                        <option key={site.ClientId} value={site.ClientId}>
                          {site.ClientName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-sm-4">
                    <select
                      className="form-select custom_toolbar_control"
                      value={bookingOptions.propertyValue}
                      onChange={(e) =>
                        setBookingOptions({
                          ...bookingOptions,
                          propertyValue: e.target.value,
                        })
                      }
                    >
                      <option value="">Select Site</option>
                      {siteOptions?.map((site) => (
                        <option
                          key={site.PropertyMasterId}
                          value={site.PropertyMasterId}
                        >
                          {site.PropertyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Tooltip
                    title="Get Booking Reports"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#fff",
                          color: "#000",
                          fontSize: "12px",
                          boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <button
                      disabled={!bookingOptions.clientValue}
                      className="btn-primary px-3 custom_toolbar_btn"
                      onClick={() => {
                        handleGetAllBookingReport(bookingOptions);
                      }}
                    >
                      Get Booking Reports
                    </button>
                  </Tooltip>
                </div>
               
              </div> */}
              <div className="row py-3 align-items-center g-2">
                <h3 className="fw-semibold mt-4 mb-0 text-start ">
                  View Booking Reports
                </h3>
              </div>

              <div className="row py-3 align-items-center g-2">
                {/* Search Bar */}
                <div className="custom_toolbar_search col-6 col-md-2">
                  <img src={searchIcon} />
                  <input
                    type="text"
                    className="form-control custom_toolbar_control"
                    id="floatingInput"
                    placeholder="Search..."
                    value={toolbar.searchText}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, searchText: e.target.value })
                    }
                  />
                </div>

                {/* Dropdowns */}
                <div className="col-6" style={{ width: "180px" }}>
                  {/* <select
                    className="form-select custom_toolbar_control"
                    value={toolbar.name}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, name: e.target.value })
                    }
                  >
                    <option value="Name">Site Name</option>
                    {[
                      ...new Set(
                        bookingreportData
                          ?.map((user) => user.PropertyName)
                          .filter(Boolean),
                      ),
                    ]
                      .sort((a, b) => (a || "").localeCompare(b || ""))
                      .map((cityItem, index) => (
                        <option key={index} value={cityItem}>
                          {cityItem}
                        </option>
                      ))}
                  </select> */}

                  {/* <select
                    className="form-select custom_toolbar_control"
                    value={bookingOptions.clientValue}
                    // onChange={(e) => {
                    //   setBookingOptions({
                    //     ...bookingOptions,
                    //     clientValue: e.target.value,
                    //     propertyValue: "",
                    //   });
                    onChange={(e) => {
                      const selectedClientId = e.target.value;

                      setBookingOptions({
                        ...bookingOptions,
                        clientValue: selectedClientId,
                        propertyValue: "",
                      });

                      if (selectedClientId === clientObj?.clientId) {
                        setFilteredSiteOptions(siteOptions);
                      } else {
                        const selectedClient = clientOptions.find(
                          (client) => client.ClientId === e.target.value,
                        );
                        const filteredProperties = siteOptions.filter(
                          (property) =>
                            property.ClientName === selectedClient?.ClientName,
                        );
                        setFilteredSiteOptions(filteredProperties);
                      }
                    }}
                  >
                    <option value="">Select Client</option>
                    {clientOptions?.map((site) => (
                      <option key={site.ClientId} value={site.ClientId}>
                        {site.ClientName}
                      </option>
                    ))}
                  </select> */}
                  <AutocompleteInput
                    masterList={clientOptions.map((client) => ({
                      name: client.ClientName,
                      value: client.ClientId,
                    }))}
                    getOptionLabel={(item) => item?.name || ""}
                    getOptionValue={(item) => item?.value || ""}
                    value={bookingOptions.clientValue}
                    onValueChange={(value) =>
                      (function () {
                        const e = { target: { value } };

                        const selectedClientId = e.target.value;

                        setBookingOptions({
                          ...bookingOptions,
                          clientValue: selectedClientId,
                          propertyValue: "",
                        });

                        if (selectedClientId === clientObj?.clientId) {
                          setFilteredSiteOptions(siteOptions);
                        } else {
                          const selectedClient = clientOptions.find(
                            (client) => client.ClientId === selectedClientId,
                          );

                          const filteredProperties = siteOptions.filter(
                            (property) =>
                              property.ClientName ===
                              selectedClient?.ClientName,
                          );

                          setFilteredSiteOptions(filteredProperties);
                        }
                      })()
                    }
                    placeholder="Select Client"
                    inputClassName="form-control custom_toolbar_control mt-1"
                    notFoundText="Client not found"
                  />
                </div>
                <div className="col-6 " style={{ width: "210px" }}>
                  {/* <select
                    className="form-select custom_toolbar_control"
                    value={bookingOptions.propertyValue}
                    onChange={(e) =>
                      setBookingOptions({
                        ...bookingOptions,
                        propertyValue: e.target.value,
                      })
                    }
                  >
                    <option value="">Select Site</option>
                    {filteredSiteOptions?.map((site) => (
                      <option
                        key={site.PropertyMasterId}
                        value={site.PropertyMasterId}
                      >
                        {site.PropertyName}
                      </option>
                    ))}
                  </select> */}

                  <AutocompleteInput
                    masterList={filteredSiteOptions.map((site) => ({
                      name: site.PropertyName,
                      value: site.PropertyMasterId,
                    }))}
                    getOptionLabel={(item) => item?.name || ""}
                    getOptionValue={(item) => item?.value || ""}
                    value={bookingOptions.propertyValue}
                    onValueChange={(value) =>
                      setBookingOptions({
                        ...bookingOptions,
                        propertyValue: value || "",
                      })
                    }
                    placeholder="Select Site"
                    inputClassName="form-control custom_toolbar_control mt-1"
                    notFoundText="Site not found"
                  />
                </div>
                <div className="col-6 " style={{ width: "180px" }}>
                  <select
                    className="form-select custom_toolbar_control"
                    value={toolbar.IsAcknowledged}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, IsAcknowledged: e.target.value })
                    }
                  >
                    <option value="Acknowledged">Acknowledgement</option>
                    <option value="Yes">Acknowledged</option>
                    <option value="No">Not Acknowledged</option>
                  </select>
                </div>
                <div className="col-6" style={{ width: "140px" }}>
                  <select
                    className="form-select custom_toolbar_control"
                    value={toolbar.reminder}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, reminder: e.target.value })
                    }
                  >
                    <option value="Reminder Sent">Reminder</option>
                    <option value="Yes">Sent</option>
                    <option value="No">Not Sent</option>
                  </select>
                </div>
                <div className="col-6 " style={{ width: "140px" }}>
                  <select
                    className="form-select custom_toolbar_control"
                    value={toolbar.booking}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, booking: e.target.value })
                    }
                  >
                    <option value="Bookings Done">Booking</option>
                    <option value="Yes">Confirmed</option>
                    <option value="No">Not Confirmed</option>
                  </select>
                </div>
                <div className="col-6 " style={{ width: "140px" }}>
                  <select
                    className="form-select custom_toolbar_control"
                    value={toolbar.letter}
                    onChange={(e) =>
                      setToolBar({ ...toolbar, letter: e.target.value })
                    }
                  >
                    <option value="Letter Sent">Letter Served</option>
                    <option value="Yes">Sent</option>
                    <option value="No">Not Sent</option>
                  </select>
                </div>
                <div className="col-auto d-flex align-items-center gap-2 ms-2">
                  <Tooltip
                    title="Get Booking Reports"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#fff",
                          color: "#000",
                          fontSize: "12px",
                          boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                        },
                      },
                      arrow: {
                        sx: {
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <button
                      disabled={!bookingOptions.clientValue}
                      className="btn btn-dark"
                      onClick={() => {
                        handleGetAllBookingReport(bookingOptions);
                      }}
                    >
                      {/* Get Booking Reports */}
                      <img src={actionSearchIcon}></img>
                    </button>
                  </Tooltip>
                </div>
                {isAnyFilterApplied && (
                  <div style={{ width: "auto", paddingLeft: "0px" }}>
                    <IconButton
                      tabIndex={0}
                      title="Clear Filters"
                      onClick={() => {
                        setToolBar({
                          searchText: "",
                          name: "Name",
                          IsAcknowledged: "Acknowledged",
                          reminder: "Reminder Sent",
                          booking: "Bookings Done",
                          letter: "Letter Sent",
                        });
                        setBookingOptions({
                          clientValue: clientObj?.clientId || "",
                          propertyValue: "",
                        });
                        handleGetAllBookingReport({
                          clientValue: clientObj?.clientId || "",
                          propertyValue: "",
                        });

                        //   const selectedClient = clientOptions.find(
                        //   (client) => client.ClientId === clientObj?.clientId || "",
                        // );
                        // const filteredProperties = siteOptions?.filter(
                        //   (property) =>
                        //     property.ClientName === selectedClient?.ClientName);
                        // setFilteredSiteOptions(filteredProperties);

                        setFilteredSiteOptions(siteOptions);
                      }}
                    >
                      <ClearIcon sx={{ color: "#717186" }} fontSize="small" />
                    </IconButton>
                  </div>
                )}
                <div className="col-auto d-flex align-items-center gap-5 ms-auto">
                  <Tooltip
                    title="Export to Excel"
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: "#fff",
                          color: "#000",
                          fontSize: "12px",
                          boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
                        },
                      },
                      arrow: { sx: { color: "#fff" } },
                    }}
                  >
                    <button
                      className="btn btn-outline"
                      onClick={handleExportToExcel}
                      style={{ height: "38px" }}
                    >
                      <img src={downloadIcon} alt="Export" />
                    </button>
                  </Tooltip>
                </div>
              </div>

              <div className="inspection-table pt-2">
                {/* <DynamicTable
                  columnsValue={tableColumns}
                  tableData={filteredRows}
                  showSelectAll={false}
                  pageSize={10}
                  rowHeight={50}
                  // setSelectedIds={setSelectedIds}
                  disableRowSelectionOnClick
                  localeText={{
                    noRowsLabel: "No data found",
                  }}
                  module="actionPlan"
                  pageSizeOptions={[100, 150, 200, { value: -1, label: "All" }]}
                /> */}
                <DynamicTable
                  columnsValue={tableColumns}
                  tableData={filteredRows}
                  pageSize={10}
                  rowHeight={50}
                  localeText={{
                    noRowsLabel: "No data found",
                  }}
                  pageSizeOptions={[100, 150, 200, { value: -1, label: "All" }]}
                />
              </div>
            </div>
            <div className="button-container text-end">
              <button
                type="button"
                className="btn-secondary col-sm-5 col-md-2"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </div>
          <Footer />
        </div>
      )}
    </>
  );
}
